/**
 * SelectOptionsEditor - Editor for select property type options
 *
 * Provides a simple interface for adding, editing, and removing select options.
 * Values are auto-generated from labels using snake_case.
 *
 * Architecture:
 * - OptionRow: Individual editable option with delete button and loading state
 * - SelectOptionsEditor: Container managing the list and "add new" input
 */

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, XClose } from '@untitledui/icons';
import { useCallback, useMemo, useRef, useState } from 'react';

import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup, InputPrefix } from '@/lib/base/forms/input/input-group';
import { Label } from '@/lib/base/forms/input/label';
import { GripVertical } from '@/lib/base/foundations/grip-vertical';

/**
 * Small inline spinner for button loading states.
 */
function SmallSpinner() {
  return (
    <svg
      className='animate-spin size-5 text-tertiary'
      viewBox='0 0 24 24'
      fill='none'
    >
      <circle
        className='opacity-25'
        cx='12'
        cy='12'
        r='10'
        stroke='currentColor'
        strokeWidth='4'
      />
      <path
        className='opacity-75'
        fill='currentColor'
        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
      />
    </svg>
  );
}

// Mirrors the backend's MAX_SELECT_OPTIONS cap (apps/crm_events/services/validators/property_type_validators.py).
// Cannot import the Python constant — keep manually in sync.
export const DEFAULT_MAX_OPTIONS = 100;
export const DEFAULT_WARNING_THRESHOLD = 90;

// Canonical definition lives in @/lib/crm/components/properties/types.
// Import locally for this file's own use and re-export for backward compat.
import type { SelectOption } from '@/lib/crm/components/properties/types';

export type { SelectOption };

export interface SelectOptionsEditorProps {
  /** Current options */
  options: SelectOption[];
  /** Callback when options change */
  onChange: (options: SelectOption[]) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Validation errors keyed by option index */
  errors?: Record<number, { value?: string; label?: string }>;
  /** Maximum number of options allowed (default: 100) */
  maxOptions?: number;
  /** Threshold for showing warning (default: 90) */
  warningThreshold?: number;
  /**
   * Callback to check if an option value is in use.
   * If provided, deletion will check usage first.
   * If not provided (e.g., for new properties), deletion is immediate.
   * @returns Promise resolving to the usage count
   */
  onCheckOptionUsage?: (optionValue: string) => Promise<number>;
}

/**
 * Convert a label to snake_case value.
 */
function toSnakeCase(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

/**
 * Generate a unique value, appending suffix if needed.
 */
function generateUniqueValue(
  label: string,
  existingValues: Set<string>
): string {
  let value = toSnakeCase(label);
  if (!value) {
    return '';
  }

  let suffix = 2;
  const baseValue = value;
  while (existingValues.has(value)) {
    value = `${baseValue}_${suffix}`;
    suffix++;
  }
  return value;
}

// =============================================================================
// OptionRow - Individual editable option
// =============================================================================

interface OptionRowProps {
  option: SelectOption;
  index: number;
  onLabelChange: (newLabel: string) => void;
  onRemove: () => void;
  onCheckUsage?: () => Promise<number>;
  disabled: boolean;
  error?: string;
  dragHandleProps?: {
    listeners: ReturnType<typeof useSortable>['listeners'];
    attributes: ReturnType<typeof useSortable>['attributes'];
  } | null;
}

function OptionRow({
  option,
  index,
  onLabelChange,
  onRemove,
  onCheckUsage,
  disabled,
  error,
  dragHandleProps,
}: OptionRowProps) {
  const [isChecking, setIsChecking] = useState(false);
  const [inUseCount, setInUseCount] = useState<number | null>(null);

  const handleRemoveClick = useCallback(async () => {
    // Clear any previous "in use" error
    setInUseCount(null);

    // If no usage check callback, remove immediately
    if (!onCheckUsage) {
      onRemove();
      return;
    }

    // Check usage first
    setIsChecking(true);
    try {
      const usageCount = await onCheckUsage();
      if (usageCount > 0) {
        setInUseCount(usageCount);
      } else {
        onRemove();
      }
    } catch {
      // On error, allow removal (fail open)
      onRemove();
    } finally {
      setIsChecking(false);
    }
  }, [onCheckUsage, onRemove]);

  // Combine validation error with "in use" error
  const displayError =
    inUseCount !== null
      ? `This option is used by ${inUseCount} record${inUseCount !== 1 ? 's' : ''} and cannot be deleted.`
      : error;

  return (
    <InputGroup
      aria-label={`Option ${index + 1} label`}
      value={option.label}
      onChange={newLabel => {
        // Clear "in use" error when user edits
        setInUseCount(null);
        onLabelChange(newLabel);
      }}
      isDisabled={disabled || isChecking}
      isInvalid={!!displayError}
      hint={displayError}
      leadingAddon={
        dragHandleProps ? (
          <InputPrefix isDisabled={disabled}>
            <button
              type='button'
              {...dragHandleProps.listeners}
              {...dragHandleProps.attributes}
              disabled={disabled}
              className={`flex items-center justify-center w-6 h-6 -mx-1 touch-none rounded outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 ${
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-grab active:cursor-grabbing'
              }`}
              aria-label='Drag to reorder'
            >
              <GripVertical className='w-4 h-4 text-tertiary' />
            </button>
          </InputPrefix>
        ) : undefined
      }
      trailingAddon={
        <InputPrefix isDisabled={disabled || isChecking}>
          {isChecking ? (
            <div className='flex items-center justify-center w-6 h-6 -mx-1'>
              <SmallSpinner />
            </div>
          ) : (
            <button
              type='button'
              onClick={handleRemoveClick}
              disabled={disabled}
              className={`flex items-center justify-center w-6 h-6 -mx-1 rounded outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 ${
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:text-error cursor-pointer'
              }`}
              aria-label={`Remove ${option.label || `option ${index + 1}`}`}
            >
              <XClose className='w-4 h-4' />
            </button>
          )}
        </InputPrefix>
      }
    >
      <InputBase />
    </InputGroup>
  );
}

// =============================================================================
// SortableOptionRow - Wrapper for drag-and-drop functionality
// =============================================================================

interface SortableOptionRowProps extends OptionRowProps {
  id: string;
}

function SortableOptionRow({ id, ...props }: SortableOptionRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style}>
        <div className='border-2 border-dashed border-border-secondary rounded-lg opacity-40'>
          <div className='invisible'>
            <OptionRow {...props} dragHandleProps={null} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <OptionRow {...props} dragHandleProps={{ listeners, attributes }} />
    </div>
  );
}

// =============================================================================
// OptionRowPreview - Preview shown during drag
// =============================================================================

interface OptionRowPreviewProps {
  label: string;
}

function OptionRowPreview({ label }: OptionRowPreviewProps) {
  return (
    <div className='bg-bg-primary border border-border-primary rounded-lg shadow-lg px-3 py-2'>
      <div className='flex items-center gap-2'>
        <GripVertical className='w-4 h-4 text-tertiary' />
        <span className='text-sm text-primary'>{label}</span>
      </div>
    </div>
  );
}

// =============================================================================
// SelectOptionsEditor - Main component
// =============================================================================

export function SelectOptionsEditor({
  options,
  onChange,
  disabled = false,
  errors = {},
  maxOptions = DEFAULT_MAX_OPTIONS,
  warningThreshold = DEFAULT_WARNING_THRESHOLD,
  onCheckOptionUsage,
}: SelectOptionsEditorProps) {
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const addInputRef = useRef<HTMLInputElement>(null);

  // DnD sensors setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Active option for drag overlay
  const activeOption = activeId
    ? options.find(opt => opt.value === activeId)
    : null;

  // Derived state
  const isAtLimit = options.length >= maxOptions;
  const isNearLimit = options.length >= warningThreshold && !isAtLimit;
  const existingValues = useMemo(
    () => new Set(options.map(opt => opt.value)),
    [options]
  );
  const existingLabelsLower = useMemo(
    () => new Set(options.map(opt => opt.label.trim().toLowerCase())),
    [options]
  );
  const canAdd = newOptionLabel.trim().length > 0 && !disabled;

  // Add a new option
  const handleAddOption = useCallback(() => {
    const label = newOptionLabel.trim();
    if (!label) {
      return;
    }

    // Check limit
    if (options.length >= maxOptions) {
      setAnnouncement('Maximum options limit reached.');
      return;
    }

    // Check for duplicate label (case-insensitive)
    if (existingLabelsLower.has(label.toLowerCase())) {
      setAnnouncement('An option with this label already exists.');
      return;
    }

    // Generate unique value
    const value = generateUniqueValue(label, existingValues);
    if (!value) {
      setAnnouncement('Invalid label - must contain letters or numbers.');
      return;
    }

    // Add the option
    onChange([...options, { value, label }]);
    setNewOptionLabel('');
    setAnnouncement('Option added.');

    // Keep focus on input for quick successive adds
    addInputRef.current?.focus();
  }, [
    newOptionLabel,
    options,
    onChange,
    maxOptions,
    existingValues,
    existingLabelsLower,
  ]);

  // Handle Enter key on add input
  const handleAddKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddOption();
      }
    },
    [handleAddOption]
  );

  // Update an existing option's label (value stays immutable)
  const handleUpdateLabel = useCallback(
    (index: number, newLabel: string) => {
      const newOptions = options.map((opt, i) =>
        i === index ? { ...opt, label: newLabel } : opt
      );
      onChange(newOptions);
    },
    [options, onChange]
  );

  // Remove an option
  const handleRemoveOption = useCallback(
    (index: number) => {
      const removedLabel = options[index]?.label || `option ${index + 1}`;
      const newOptions = options.filter((_, i) => i !== index);
      onChange(newOptions);
      setAnnouncement(`${removedLabel} removed.`);
    },
    [options, onChange]
  );

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex(opt => opt.value === active.id);
      const newIndex = options.findIndex(opt => opt.value === over.id);
      onChange(arrayMove(options, oldIndex, newIndex));
      // Announce reorder for screen readers using existing aria-live region
      const movedLabel = options[oldIndex]?.label || 'Option';
      setAnnouncement(`${movedLabel} moved to position ${newIndex + 1}`);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <div className='space-y-3'>
      {/* Aria-live region for announcements */}
      <div aria-live='polite' className='sr-only'>
        {announcement}
      </div>

      {/* Header with label and count */}
      <div className='flex items-center justify-between'>
        <Label>Options</Label>
        <span className='text-xs text-tertiary'>
          {options.length} / {maxOptions}
        </span>
      </div>

      {/* Warning banner */}
      {isNearLimit && (
        <div
          role='alert'
          className='p-3 bg-warning-50 border border-warning-200 rounded-lg'
        >
          <p className='text-sm text-warning-700'>
            Approaching options limit ({options.length}/{maxOptions}).
          </p>
        </div>
      )}

      {/* Error banner */}
      {isAtLimit && (
        <div
          role='alert'
          className='p-3 bg-error-50 border border-error-200 rounded-lg'
        >
          <p className='text-sm text-error-700'>
            Maximum of {maxOptions} options reached.
          </p>
        </div>
      )}

      {/* Empty state */}
      {options.length === 0 && !isAtLimit && (
        <div className='py-4 px-3 text-center border border-dashed border-border-secondary rounded-lg'>
          <p className='text-sm text-tertiary'>
            No options yet. Add your first option below.
          </p>
        </div>
      )}

      {/* Existing options list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={options.map(opt => opt.value)}
          strategy={verticalListSortingStrategy}
        >
          {options.map((option, index) => (
            <SortableOptionRow
              key={option.value}
              id={option.value}
              option={option}
              index={index}
              onLabelChange={newLabel => handleUpdateLabel(index, newLabel)}
              onRemove={() => handleRemoveOption(index)}
              onCheckUsage={
                onCheckOptionUsage
                  ? () => onCheckOptionUsage(option.value)
                  : undefined
              }
              disabled={disabled}
              error={errors[index]?.value || errors[index]?.label}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {activeOption ? (
            <div className='opacity-90 shadow-lg rounded-lg'>
              <OptionRowPreview label={activeOption.label} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Add new option input with button */}
      {!isAtLimit && (
        <InputGroup
          aria-label='New option label'
          value={newOptionLabel}
          onChange={setNewOptionLabel}
          isDisabled={disabled}
          trailingAddon={
            <InputPrefix isDisabled={disabled}>
              <button
                type='button'
                onClick={handleAddOption}
                disabled={!canAdd}
                className={`flex items-center justify-center w-6 h-6 -mx-1 rounded outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 ${
                  !canAdd
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:text-brand cursor-pointer'
                }`}
                aria-label='Add option'
              >
                <Plus className='w-4 h-4' />
              </button>
            </InputPrefix>
          }
        >
          <InputBase
            ref={addInputRef}
            placeholder='Type option label'
            onKeyDown={handleAddKeyDown}
          />
        </InputGroup>
      )}
    </div>
  );
}
